import { PlusIcon, MinusIcon } from '@heroicons/react/24/solid';
import { FormFieldError } from '@kira/checkpoint-shared-frontend';
import React from 'react';
import { FieldError } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import LabelWithTooltip from 'pages/Overview/LabelWithTooltip';

interface CounterInputProps {
  errors?: FieldError;
  label?: string;
  setValue: (value: number) => void;
  tooltipText?: string;
  value: number;
  suffix?: string;
  increments?: number;
  max?: number;
  isIntegerOnly?: boolean;
  inputClassName?: string;
  minusClickerId?: string;
  plusClickerId?: string;
  idLength?: string;
}

const CounterInput: React.FC<CounterInputProps> = ({
  errors,
  label,
  setValue,
  tooltipText,
  value,
  suffix = '',
  increments = 1,
  max,
  isIntegerOnly,
  inputClassName,
  minusClickerId = '',
  plusClickerId = '',
  idLength = '',
}) => {
  const handleSubtractOne = () => {
    if (value > 0) {
      setValue(value - increments);
    }
  };

  const handleAddOne = () => {
    if (!max || value < max) {
      setValue(value + increments);
    }
  };

  const inputClassNames = twMerge(
    'border-none',
    'border-t-0',
    'border-x-0',
    'focus:border-none',
    'focus:ring-0',
    'p-0',
    'prose-body-md-reg',
    'text-center',
    'text-kira-slate-900',
    'grow',
    'bg-transparent',
    inputClassName
  );

  const iconClassNames = twMerge(
    'duration-200',
    'ease-in-out',
    'h-6',
    'hover:bg-kira-blue-100',
    'hover:cursor-pointer',
    'inline-block',
    'p-1',
    'rounded',
    'w-6'
  );

  const containerClassNames = twMerge(
    'flex',
    'w-64',
    'flex-col',
    'align-middle',
    inputClassName || ''
  );

  const inputValue = isIntegerOnly ? value : value.toFixed(1);

  return (
    <div className={containerClassNames}>
      {label && (
        <div className="-mb-1.5">
          <LabelWithTooltip label={label} tooltipText={tooltipText || ''} />
        </div>
      )}
      <div className="flex justify-between border-b p-1">
        <MinusIcon
          className={iconClassNames}
          onClick={handleSubtractOne}
          data-testid={minusClickerId}
        />
        <input
          className={inputClassNames}
          disabled
          inputMode="numeric"
          type="text"
          value={`${inputValue}${suffix}`}
          data-testid={idLength}
        />
        <PlusIcon
          className={iconClassNames}
          onClick={handleAddOne}
          data-testid={plusClickerId}
        />
      </div>
      <FormFieldError isVisible={!!errors} message={errors?.message ?? ''} />
    </div>
  );
};
export default CounterInput;
